import { useCallback, useRef } from 'react';

const STOP_ITERABLE_EVENT_DRIVEN_PROMISE_EVENT = 'STOP_ITERABLE_EVENT_DRIVEN_PROMISE_EVENT';

/**
 * If you are reading this, I am sorry... but also, it works! 🤷‍♂
 *
 * This hook creates a promise that can be resolved by custom DOM event and exposes functions for doing so.
 * This hack is useful when you need to wait for some operation to finish (let's say a user editing some) before continuing the execution of an async function.
 * It is currently used in the dragAndDrop editor to help pause a bee hook execution until the user finishes editing a bee row.
 *
 * NOTE: the current implementation does not support having multiple promises at the same time as resolving the promise will resolve all promises created by this hook.
 *
 * @example
 * const { promiseRef, startEvent, stopEvent } = useEventDrivenPromise();
 *
 * startEvent();
 *
 * const someAsyncFunctionWhichNeedsToWaitForAnotherProcessToFinish = async () => {
 *  await promiseRef.current; // This will wait until something call the `stopEvent` function to resolve the promise
 *  doSomethingElse(); // This will only be executed after the promise is resolved
 * }
 */
export const useEventDrivenPromise = () => {
  const promiseRef = useRef<Promise<boolean>>(new Promise((resolve) => resolve(false)));

  const startEvent = useCallback(() => {
    promiseRef.current = new Promise((resolve) => {
      window.addEventListener(STOP_ITERABLE_EVENT_DRIVEN_PROMISE_EVENT, function listener() {
        resolve(true);
        window.removeEventListener(STOP_ITERABLE_EVENT_DRIVEN_PROMISE_EVENT, listener);
      });
    });
  }, []);

  const stopEvent = useCallback(() => {
    window.dispatchEvent(new Event(STOP_ITERABLE_EVENT_DRIVEN_PROMISE_EVENT));
  }, []);

  return {
    promiseRef,
    startEvent,
    stopEvent,
  };
};
